import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { FaYoutube, FaTiktok, FaFacebook, FaInstagram, FaUser, FaStore, FaCog, FaSignOutAlt, FaShoppingCart, FaHeart, FaBars, FaTimes, FaHome } from 'react-icons/fa';
import ProductCard from './ProductCard';
import { getImageUrl, apiGet } from '../utils/api';
import DarkModeToggle from './DarkModeToggle';
import { useDarkMode } from '../contexts/DarkModeContext';

const ModernHeader = () => {
  const { user, hydrated, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useDarkMode();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeout = useRef();
  const searchInputRef = useRef();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);



  // Live search products as user types with rate limiting
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      setIsSearching(false);
      return;
    }
    
    // Rate limiting: only search if query is at least 2 characters and not too frequent
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      setIsSearching(false);
      return;
    }
    
    setShowDropdown(true);
    setIsSearching(true);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        // Sanitize search query to prevent injection
        const sanitizedQuery = searchQuery.trim().replace(/[<>]/g, '').substring(0, 100);
        const res = await apiGet(`/api/products?search=${encodeURIComponent(sanitizedQuery)}&limit=12`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        setSearchResults(Array.isArray(data) ? data.filter(p => p.status === 'published') : []);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500); // Increased debounce time for better rate limiting
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery]);

  // Close dropdown on outside click or escape
  useEffect(() => {
    function handleClick(e) {
      if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      // Close profile dropdown when clicking outside (both mobile and desktop)
      if (dropdownOpen && !e.target.closest('.mobile-profile-dropdown') && !e.target.closest('.desktop-profile-dropdown') && !e.target.closest('.profile-button')) {
        setDropdownOpen(false);
      }
      // Close search modal when clicking outside
      if (searchModalOpen && !e.target.closest('.search-modal') && !e.target.closest('.search-icon-button')) {
        setSearchModalOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    }
    function handleEsc(e) {
      if (e.key === 'Escape') {
        setShowDropdown(false);
        setDropdownOpen(false);
        setSearchModalOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    }
    if (showDropdown || dropdownOpen || searchModalOpen) {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showDropdown, dropdownOpen, searchModalOpen]);

  const handleLogout = async () => {
    await logout();
    setDropdownOpen(false);
    // No window.location.reload();
  };

  // Platform icon helper with error handling
  const getPlatformIcon = (type) => {
    switch (type) {
      case 'youtube':
        return <img src="/images/categories/youtube.svg" alt="YouTube" className="w-6 h-6" width="24" height="24" onError={(e) => { e.target.style.display = 'none'; }} />;
      case 'instagram':
        return <img src="/images/categories/instagram.svg" alt="Instagram" className="w-6 h-6" width="24" height="24" onError={(e) => { e.target.style.display = 'none'; }} />;
      case 'tiktok':
        return <img src="/images/categories/tiktok.svg" alt="TikTok" className="w-6 h-6" width="24" height="24" onError={(e) => { e.target.style.display = 'none'; }} />;
      case 'facebook':
        return <img src="/images/categories/facebook.svg" alt="Facebook" className="w-6 h-6" width="24" height="24" onError={(e) => { e.target.style.display = 'none'; }} />;
      default:
        return null;
    }
  };

  // Generate avatar initials from username/name
  const getAvatarInitials = (user) => {
    const name = user?.displayName || user?.username || user?.email || 'User';
    return name.substring(0, 2).toUpperCase();
  };

  // Get avatar URL with fallback
  const getAvatarUrl = (user) => {
    if (user?.avatar || user?.photoURL) {
      return user.avatar || user.photoURL;
    }
    // Use original color scheme - light gray background with dark text
    const name = user?.displayName || user?.username || user?.email || 'User';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=E5E7EB&color=374151&size=200&bold=true&length=2`;
  };

  if (!hydrated) {
    return null; // Or a spinner if you prefer
  }

  return (
    <header className={`backdrop-blur-md shadow-lg sticky top-0 z-50 border-b transition-colors duration-300 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-[#2a2a2a] via-[#1a1a1a] to-[#2a2a2a] border-gray-700/50' 
        : 'bg-white/70 border-blue-100'
    }`}>
              {/* Mobile/Tablet Header */}
        <div className="flex md:hidden items-center justify-between px-4 py-2">
          {/* Hamburger */}
          <button
            className={`p-2 rounded-full shadow-md focus:outline-none transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-[#3f3f3f]/80 text-gray-300' 
                : 'bg-white/80 text-blue-700'
            }`}
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <FaBars className="w-6 h-6" />
          </button>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img src="/images/logo.png" alt="YTProud" className="h-10 w-auto drop-shadow-md" width="120" height="40" />
        </Link>
        {/* Search Icon */}
        <button
          className={`p-2 rounded-full shadow-md focus:outline-none transition-colors duration-300 ${
            isDarkMode 
              ? 'bg-[#3f3f3f]/80 text-gray-300' 
              : 'bg-white/80 text-blue-700'
          }`}
          onClick={() => setSearchModalOpen(true)}
          aria-label="Open search"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
        {/* Dark Mode Toggle - Mobile */}
        <DarkModeToggle className="mr-2" />
        
        {/* Profile Icon - Mobile Only */}
        {user ? (
          <button
            className={`p-2 rounded-full shadow-md focus:outline-none profile-button transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-[#3f3f3f]/80' 
                : 'bg-white/80'
            }`}
            onClick={() => setDropdownOpen(open => !open)}
            aria-label="Profile menu"
          >
            <img
              src={getAvatarUrl(user)}
              alt="avatar"
              className="w-8 h-8 rounded-full object-cover border-2 border-gray-200 shadow-sm bg-gray-100"
              width="32"
              height="32"
              onError={(e) => {
                // Fallback to initials if image fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div 
              className="w-8 h-8 rounded-full border-2 border-gray-200 shadow-sm bg-gray-100 text-gray-700 text-xs font-bold flex items-center justify-center hidden"
              style={{ display: 'none' }}
            >
              {getAvatarInitials(user)}
            </div>
          </button>
        ) : (
          <Link
            to="/login"
            className="p-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md focus:outline-none profile-button"
            aria-label="Login"
          >
            <FaUser className="w-6 h-6" />
          </Link>
        )}
      </div>
      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex">
          <div className="w-4/5 max-w-xs h-full flex flex-col items-stretch relative animate-slideInLeft">
            <div className={`flex-1 flex flex-col backdrop-blur-xl shadow-2xl rounded-3xl p-6 mt-4 mx-2 transition-colors duration-300 ${
              isDarkMode 
                ? 'bg-gradient-to-br from-[#2a2a2a] via-[#1a1a1a] to-[#2a2a2a]' 
                : 'bg-white'
            }`}>
              <button
                className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 text-blue-700"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <FaTimes className="w-6 h-6" />
              </button>
              <nav className="flex flex-col gap-4 mt-10">
                <Link to="/" className={`flex items-center gap-3 text-lg font-semibold transition-colors duration-300 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`} onClick={() => setMobileMenuOpen(false)}><FaHome className="w-5 h-5" /> Home</Link>
                <Link to="/marketplace" className={`flex items-center gap-3 text-lg font-semibold transition-colors duration-300 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`} onClick={() => setMobileMenuOpen(false)}><FaStore className="w-5 h-5" /> Marketplace</Link>
                <Link to="/buy-youtube-channel" className={`flex items-center gap-3 text-base pl-2 transition-colors duration-300 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} onClick={() => setMobileMenuOpen(false)}><FaYoutube className="w-5 h-5 text-red-600" /> Buy YouTube Channel</Link>
                <Link to="/buy-tiktok-account" className={`flex items-center gap-3 text-base pl-2 transition-colors duration-300 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} onClick={() => setMobileMenuOpen(false)}><FaTiktok className="w-5 h-5 text-black" /> Buy TikTok Account</Link>
                <Link to="/buy-facebook-page" className={`flex items-center gap-3 text-base pl-2 transition-colors duration-300 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} onClick={() => setMobileMenuOpen(false)}><FaFacebook className="w-5 h-5 text-blue-600" /> Buy Facebook Page</Link>
                <Link to="/buy-instagram-account" className={`flex items-center gap-3 text-base pl-2 transition-colors duration-300 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} onClick={() => setMobileMenuOpen(false)}><FaInstagram className="w-5 h-5 text-pink-500" /> Buy Instagram Account</Link>
                <div className={`border-t my-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
                {user ? (
                  <>
                    <Link to="/profile" className={`flex items-center gap-3 text-base transition-colors duration-300 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} onClick={() => setMobileMenuOpen(false)}><FaUser className="w-5 h-5" /> My Account</Link>
                    <Link to="/orders" className={`flex items-center gap-3 text-base transition-colors duration-300 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} onClick={() => setMobileMenuOpen(false)}><FaShoppingCart className="w-5 h-5" /> My Order</Link>
                    <Link to="/wishlist" className={`flex items-center gap-3 text-base transition-colors duration-300 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} onClick={() => setMobileMenuOpen(false)}><FaHeart className="w-5 h-5 text-pink-500" /> Wishlist</Link>
                    <button className="flex items-center gap-3 text-base text-red-600" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}><FaSignOutAlt className="w-5 h-5" /> Logout</button>
                  </>
                ) : (
                  <Link to="/login" className={`flex items-center gap-3 font-semibold transition-colors duration-300 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`} onClick={() => setMobileMenuOpen(false)}><FaUser className="w-5 h-5" /> Login</Link>
                )}
              </nav>
            </div>
          </div>
          <div className="flex-1" onClick={() => setMobileMenuOpen(false)} />
        </div>
      )}

      {/* Mobile Profile Dropdown */}
      {dropdownOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-end pt-20 pr-4 md:hidden">
          <div className="bg-white dark:bg-[#282828] dark:text-gray-100 dark:border dark:border-gray-700 rounded-2xl shadow-2xl dark:shadow-black/30 p-4 w-64 animate-fadeIn mobile-profile-dropdown">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
              <img
                src={getAvatarUrl(user)}
                alt="avatar"
                className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600 shadow-sm bg-gray-100 dark:bg-gray-800"
                width="48"
                height="48"
                onError={(e) => {
                  // Fallback to initials if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div 
                className="w-12 h-12 rounded-full border-2 border-gray-200 dark:border-gray-600 shadow-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-bold flex items-center justify-center hidden"
                style={{ display: 'none' }}
              >
                {getAvatarInitials(user)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900 dark:text-white truncate">
                  {user?.role === 'admin' ? 'Admin' : (user?.displayName || user?.username || 'User')}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{user?.email}</div>
              </div>
            </div>
            <div className="space-y-2">
              {user?.role === 'admin' && (
                <button
                  className="w-full text-left px-4 py-3 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-xl font-semibold flex items-center gap-3"
                  onClick={() => { setDropdownOpen(false); navigate('/admin@admin'); }}
                >
                  <FaCog className="w-5 h-5" />
                  <span>Admin Dashboard</span>
                </button>
              )}
              <Link to="/profile" className="flex items-center gap-3 px-4 py-3 text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-xl transition" onClick={() => setDropdownOpen(false)}>
                <FaUser className="w-5 h-5 text-gray-700 dark:text-gray-400" />
                <span>Profile</span>
              </Link>
              <Link to="/cart" className="flex items-center gap-3 px-4 py-3 text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-xl transition" onClick={() => setDropdownOpen(false)}>
                <FaShoppingCart className="w-5 h-5 text-blue-600" />
                <span>Cart</span>
              </Link>
              <Link to="/wishlist" className="flex items-center gap-3 px-4 py-3 text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-xl transition" onClick={() => setDropdownOpen(false)}>
                <FaHeart className="w-5 h-5 text-red-500" />
                <span>Wishlist</span>
              </Link>
              <Link to="/sell-account" className="flex items-center gap-3 px-4 py-3 text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-xl transition" onClick={() => setDropdownOpen(false)}>
                <FaStore className="w-5 h-5 text-gray-700 dark:text-gray-400" />
                <span>Sell Account</span>
              </Link>
              <button
                className="flex items-center gap-3 px-4 py-3 text-red-500 hover:bg-red-50 w-full transition rounded-xl"
                onClick={() => { handleLogout(); setDropdownOpen(false); }}
              >
                <FaSignOutAlt className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Desktop Header (unchanged) */}
      <div className="hidden md:flex max-w-[1650px] mx-auto items-center justify-between px-6 py-3">
        {/* Left Side - Logo and Navigation */}
        <div className="flex items-center gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img src="/images/logo.png" alt="YTProud" className="h-12 w-auto drop-shadow-md" width="144" height="48" />
        </Link>
        {/* Nav Links */}
          <nav className="hidden md:flex items-center gap-2">
          <Link
            to="/"
            className={`px-4 py-2 rounded-full font-semibold transition flex items-center gap-2 ${location.pathname === '/' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-lg shadow-red-500/25' : 'bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#3a3a3a] shadow-lg'}`}
          >
            <FaHome className="w-4 h-4" />
            Home
          </Link>
          <Link
            to="/marketplace"
            className={`px-4 py-2 rounded-full font-semibold transition flex items-center gap-2 ${location.pathname.startsWith('/marketplace') ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-lg shadow-red-500/25' : 'bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#3a3a3a] shadow-lg'}`}
          >
            <FaStore className="w-4 h-4" />
            Marketplace
          </Link>
          <Link
            to="/buy-youtube-channel"
            className={`px-4 py-2 rounded-full font-semibold transition flex items-center gap-2 ${location.pathname === '/buy-youtube-channel' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-lg shadow-red-500/25' : 'bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#3a3a3a] shadow-lg'}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            YouTube
          </Link>
          <Link
            to="/buy-tiktok-account"
            className={`px-4 py-2 rounded-full font-semibold transition flex items-center gap-2 ${location.pathname === '/buy-tiktok-account' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-lg shadow-red-500/25' : 'bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#3a3a3a] shadow-lg'}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
            </svg>
            TikTok
          </Link>
          <Link
            to="/buy-instagram-account"
            className={`px-4 py-2 rounded-full font-semibold transition flex items-center gap-2 ${location.pathname === '/buy-instagram-account' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-lg shadow-red-500/25' : 'bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#3a3a3a] shadow-lg'}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            Instagram
          </Link>
          <Link
            to="/buy-facebook-page"
            className={`px-4 py-2 rounded-full font-semibold transition flex items-center gap-2 ${location.pathname === '/buy-facebook-page' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-lg shadow-red-500/25' : 'bg-white dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#3a3a3a] shadow-lg'}`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Facebook
          </Link>
        </nav>
        </div>

                {/* Right Side - Consistent Spacing System */}
        <div className="flex items-center gap-4">
          {/* Dark Mode Toggle */}
          <DarkModeToggle />
          
          {/* Search Icon */}
          <button
            onClick={() => setSearchModalOpen(true)}
            className="search-icon-button inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/80 dark:bg-[#111111] border border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-[#1a1a1a] transition-all duration-200 shadow-md hover:shadow-lg"
            title="Search products"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
                  </button>
        
        {/* Chat Icon, Profile, Sell Account - only if logged in */}
        {user ? (
          <>
            <button
              onClick={() => navigate('/messages')}
                className={`inline-flex items-center justify-center w-10 h-10 rounded-full border shadow-md transition relative group ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 border-blue-700' 
                  : 'bg-gradient-to-br from-blue-100 to-blue-300 hover:from-blue-200 hover:to-blue-400 border-blue-200'
              }`}
              title="Go to Chat"
            >
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
            </button>
            <div className="flex items-center gap-3">
              {/* Profile Dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center gap-2 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/50 transition group cursor-pointer profile-button"
                  title="Profile"
                  onClick={() => setDropdownOpen(open => !open)}
                >
                  <img
                    src={getAvatarUrl(user)}
                    alt="avatar"
                    className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 shadow-sm bg-gray-100"
                    width="40"
                    height="40"
                    onError={(e) => {
                      // Fallback to initials if image fails to load
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div 
                    className="w-10 h-10 rounded-full border-2 border-gray-200 shadow-sm bg-gray-100 text-gray-700 text-xs font-bold flex items-center justify-center hidden"
                    style={{ display: 'none' }}
                  >
                    {getAvatarInitials(user)}
                  </div>
                  <span className="hidden md:inline text-base font-bold text-gray-900 dark:text-white max-w-[120px] truncate">
                    {user?.role === 'admin' ? 'Admin' : (user?.displayName || user?.username || 'User')}
                  </span>
                  <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-[#282828] dark:text-gray-100 dark:border dark:border-gray-700 rounded-2xl shadow-2xl dark:shadow-black/30 z-50 p-4 hidden md:block desktop-profile-dropdown">
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
                                              <img
                          src={getAvatarUrl(user)}
                          alt="avatar"
                          className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 shadow-sm bg-gray-100"
                          width="48"
                          height="48"
                          onError={(e) => {
                            // Fallback to initials if image fails to load
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      <div 
                        className="w-12 h-12 rounded-full border-2 border-gray-200 shadow-sm bg-gray-100 text-gray-700 text-sm font-bold flex items-center justify-center hidden"
                        style={{ display: 'none' }}
                      >
                        {getAvatarInitials(user)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-gray-900 dark:text-white truncate">
                          {user?.role === 'admin' ? 'Admin' : (user?.displayName || user?.username || 'User')}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{user?.email}</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {user?.role === 'admin' && (
                        <button
                          className="w-full text-left px-4 py-3 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-xl font-semibold flex items-center gap-3"
                          onClick={() => { setDropdownOpen(false); navigate('/admin@admin'); }}
                        >
                          <FaCog className="w-5 h-5" />
                          <span>Admin Dashboard</span>
                        </button>
                      )}
                      <Link to="/profile" className="flex items-center gap-3 px-4 py-3 text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-xl transition" onClick={() => setDropdownOpen(false)}>
                        <FaUser className="w-5 h-5 text-gray-700 dark:text-gray-400" />
                        <span>Profile</span>
                      </Link>
                      <Link to="/cart" className="flex items-center gap-3 px-4 py-3 text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-xl transition" onClick={() => setDropdownOpen(false)}>
                        <FaShoppingCart className="w-5 h-5 text-blue-600" />
                        <span>Cart</span>
                      </Link>
                      <Link to="/wishlist" className="flex items-center gap-3 px-4 py-3 text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-xl transition" onClick={() => setDropdownOpen(false)}>
                        <FaHeart className="w-5 h-5 text-red-500" />
                        <span>Wishlist</span>
                      </Link>
                      <Link to="/sell-account" className="flex items-center gap-3 px-4 py-3 text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/50 rounded-xl transition" onClick={() => setDropdownOpen(false)}>
                        <FaStore className="w-5 h-5 text-gray-700 dark:text-gray-400" />
                        <span>Sell Account</span>
                      </Link>
                      <button
                        className="flex items-center gap-3 px-4 py-3 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/50 w-full transition rounded-xl"
                        onClick={() => { handleLogout(); setDropdownOpen(false); }}
                      >
                        <FaSignOutAlt className="w-5 h-5" />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold px-6 py-2 rounded-full shadow-lg transition text-base"
            >
              Login / Signup
            </Link>
          </div>
        )}
      </div>
      </div>

      {/* Search Modal */}
      {searchModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-20 search-modal">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl w-full max-w-4xl mx-4 max-h-[80vh] overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="w-10"></div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex-1 text-center">Search Accounts</h2>
              <button
                onClick={() => {
                  setSearchModalOpen(false);
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition w-10"
              >
                <svg className="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Search Input */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <form onSubmit={e => { 
                e.preventDefault(); 
                if (searchQuery.trim()) {
                  navigate(`/marketplace?search=${encodeURIComponent(searchQuery.trim())}`);
                  setSearchModalOpen(false);
                  setSearchQuery('');
                  setSearchResults([]);
                }
              }} ref={searchInputRef}>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => {
                      const sanitizedValue = e.target.value.replace(/[<>]/g, '');
                      setSearchQuery(sanitizedValue);
                    }}
                    placeholder="Search accounts..."
                    className="w-full bg-gray-50 dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-600 rounded-xl pl-14 pr-16 py-4 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    onFocus={() => { if (searchQuery.trim()) setShowDropdown(true); }}
                    maxLength={100}
                    aria-label="Search for products"
                    autoFocus
                  />
                  <div className="absolute left-5 top-1/2 -translate-y-1/2">
                    {isSearching ? (
                      <svg className="w-6 h-6 text-blue-500 animate-spin" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="31.416">
                          <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                          <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
                        </circle>
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg shadow hover:from-blue-600 hover:to-blue-800 transition font-semibold"
                  >
                    Search
                  </button>
                </div>
              </form>
            </div>

            {/* Search Results */}
            {isSearching && (
              <div className="p-12 text-center">
                <div className="inline-flex items-center gap-3">
                  <svg className="w-8 h-8 text-blue-500 animate-spin" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="31.416">
                      <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                      <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
                    </circle>
                  </svg>
                  <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">Searching accounts...</span>
                </div>
              </div>
            )}
            {showDropdown && !isSearching && searchResults.length > 0 && (
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {searchResults.map(product => (
                    <div
                      key={product._id}
                      onClick={() => { 
                        navigate(`/product/${product._id}`); 
                        setSearchModalOpen(false);
                        setSearchQuery('');
                        setSearchResults([]);
                      }}
                      className="flex flex-col bg-gray-50 dark:bg-[#2a2a2a] hover:bg-blue-50 dark:hover:bg-[#1e3a8a] rounded-xl p-4 cursor-pointer transition border border-transparent hover:border-blue-300 dark:hover:border-blue-600 shadow-sm"
                    >
                      <img
                        src={product.mainImage ? getImageUrl(product.mainImage) : product.image}
                        alt={product.title}
                        className="w-full h-32 object-cover rounded-lg mb-3 border"
                        width="300"
                        height="128"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = '/images/placeholder-product.png';
                          e.target.onerror = null;
                        }}
                      />
                      <div className="flex items-center gap-2 mb-2">
                        {getPlatformIcon(product.accountType || product.platform)}
                        <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm truncate" title={product.title}>
                          {product.title}
                        </span>
                      </div>
                      <div className="text-blue-700 dark:text-blue-400 font-bold text-lg">${product.salePrice || product.price}</div>
                    </div>
                  ))}
                </div>
                <div className="text-center mt-6">
                  <button
                    className="text-blue-600 dark:text-blue-400 hover:underline font-semibold text-lg"
                    onClick={e => { 
                      e.preventDefault(); 
                      navigate(`/marketplace?search=${encodeURIComponent(searchQuery.trim())}`); 
                      setSearchModalOpen(false);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    View all results ({searchResults.length})
                  </button>
                </div>
              </div>
            )}

            {/* No Results */}
            {showDropdown && !isSearching && searchQuery.trim() && searchResults.length === 0 && (
              <div className="p-12 text-center">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No products found</h3>
                <p className="text-gray-500 dark:text-gray-400">Try adjusting your search terms or browse our marketplace</p>
              </div>
            )}

            {/* Initial State */}
            {!showDropdown && (
              <div className="p-12 text-center">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Search for Social Media Accounts</h3>
                <p className="text-gray-500 dark:text-gray-400">Find YouTube, TikTok, Instagram & Facebook accounts</p>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default ModernHeader; 